import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { ReactComponent as MagicSpinnerBkg } from '../../images/illustrations/illustration-spinner-bkg.svg';
import { ANIMATIONS, INTERNAL_LINKS } from '../../utils';
import { spin } from '../../utils/theme/themes';
import { MagicButton } from '../code-illustrations/cells/MagicButton';
import { InfoText } from '../global/InfoText';
import { ProductIllustration } from '../global/ProductIllustration';
import {
  CellIllustration,
  FloatingText,
  Illustrations,
  Section
} from '../global/sharedStyles';

const Wrapper = styled(Section)`
  .info-text {
    max-width: 260px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    .floating-text {
      position: absolute;
    }
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    .floating-text {
      position: static;
    }
  }
`;

const MagicIllustrations = styled(Illustrations)`
  height: 515px;
  margin-top: 20px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    height: 485px;
    margin-top: 50px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    height: 225px;
  }
`;

const PopoverIllustration = styled(CellIllustration)`
  top: 105px;
  left: 852px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    left: 472px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    top: 38px;
    left: 245px;

    img {
      width: 125px;
    }
  }
`;

const SQLCellIllustration = styled(ProductIllustration)`
  top: 0;
  left: 380px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    left: 0;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    top: 0;
    left: 0;

    img {
      width: 272px;
    }
  }
`;

const PythonCellIllustration = styled(ProductIllustration)`
  top: 245px;
  left: 285px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    left: -140px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    top: 100px;
    left: 30px;

    img {
      width: 269px;
    }
  }
`;

const MagicBkg = styled.div`
  position: absolute;
  top: -270px;
  margin: 0px auto;
  width: 100%;
  display: flex;
  justify-content: center;
  pointer-events: none;

  > div {
    flex: none;
    mask: linear-gradient(0deg, transparent 50%, white 100%);

    svg {
      animation: ${spin} 120s linear infinite;

      > g {
        opacity: 0.22;
      }
    }
  }
`;

const FloatingMagic = styled.div`
  &&& {
    width: 30px;
    height: 30px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      scale: 1.5;

      > div > div > div {
        box-shadow: ${({ theme }) => theme.colors.LIGHT_PURPLE} 0px 0px 0px 1px
          inset;
      }
    }
  }
`;

export const SectionMagic: React.ComponentType = React.memo(() => {
  const theme = useTheme();
  const shouldCollapse = useMediaQuery(theme.mediaQueryString.MEDIUM);

  return (
    <Wrapper>
      <FloatingText>
        <InfoText
          description="The most intelligent AI data assistant ever made, built right into your workflows. Magic AI helps with every step of the process, from SQL to charts to entire data analyses."
          icon={
            <FloatingMagic>
              <MagicButton color="dark" state="loading" />
            </FloatingMagic>
          }
          link={INTERNAL_LINKS.Magic}
          title="Magical AI assist"
        />
      </FloatingText>
      <MagicIllustrations>
        <SQLCellIllustration
          cellLabel="SQL cell"
          hideHeader={shouldCollapse}
          imgPath="notebooks/magic-sql-cell"
          showCommentCount={true}
        />
        <PythonCellIllustration
          cellLabel="Python cell"
          hideHeader={shouldCollapse}
          hideHeaderActions={true}
          imgPath="notebooks/magic-python-cell"
        />
        <PopoverIllustration {...ANIMATIONS.Cell}>
          <img
            alt="SQL typeahead"
            src="/imzages/notebooks/magic-popover.png"
            srcSet={`/images/notebooks/magic-popover.png 1x, /images/notebooks/magic-popover@2x.png 2x`}
          />
        </PopoverIllustration>
      </MagicIllustrations>
      <MagicBkg>
        <div>
          <MagicSpinnerBkg />
        </div>
      </MagicBkg>
    </Wrapper>
  );
});
